import { useMemo } from 'react'
import { FormGroup } from '../types/CustomForm'
import { AnswerState } from '../components/DynamicForm'
import {
  TEAM_OFFICIALS_SECTION,
  PLAYER_INFO_SECTION,
  TEAM_OFFICIAL_FIELDS,
  PLAYER_INFO_FIELDS,
  ReservedField,
} from '../constants/prefillMappings'

interface SelectedPlayer {
  id?: string
  name_first?: string
  name_last?: string
  email?: string
  birth_date?: string
  [key: string]: any
}

interface PrefillResult {
  /** Pre-filled answers to merge with existing form answers */
  prefilledAnswers: AnswerState
  /** Set of field IDs that should be disabled */
  disabledFieldIds: Set<string>
}

// Helper to determine if a field is reserved (handles 1/0, true/false, etc.)
function isFieldReserved(is_reserved: any): boolean {
  // Accepts 1, true, '1', 'true' as reserved
  return (
    is_reserved === 1 ||
    is_reserved === true ||
    is_reserved === '1' ||
    is_reserved === 'true'
  )
}

/**
 * 🔄 usePrefillReservedFields: Hook to prefill and disable reserved form fields
 *
 * This hook analyzes the selected form and selectedPlayer data to automatically
 * populate reserved fields with existing player information and mark them as disabled.
 * Uses the new key-value answer format where answers are keyed by item.id.
 *
 * For player registrations, the Player Email field is always prefilled with the logged
 * user's email and disabled, regardless of whether a player is selected.
 *
 * @param selectedForm - The form definition (team_form or player_form)
 * @param selectedPlayer - The selected player/team official data (can be null for player registrations)
 * @param isTeamRep - Whether this is for team registration (vs player registration)
 * @param existingAnswers - Current form answers to avoid overwriting
 * @param loggedUserEmail - Email of the logged-in user (used for Player Email field)
 * @param loggedUserBirthDate - Birth date of the logged-in user (used for Player Birth Date field)
 * @returns Object containing prefilled answers and disabled field IDs
 */
export const usePrefillReservedFields = (
  selectedForm: { groups: FormGroup[] } | null,
  selectedPlayer: SelectedPlayer | null,
  isTeamRep: boolean,
  existingAnswers: AnswerState = {},
  loggedUserEmail: string = '',
  loggedUserBirthDate: string | null = null
): PrefillResult => {
  return useMemo(() => {
    console.log('🔄 usePrefillReservedFields: Processing prefill logic', {
      hasSelectedForm: !!selectedForm,
      hasSelectedPlayer: !!selectedPlayer,
      isTeamRep,
      existingAnswersCount: Object.keys(existingAnswers).length,
      selectedPlayer,
    })

    const prefilledAnswers: AnswerState = {}
    const disabledFieldIds = new Set<string>()

    // Early return if no form definition
    if (!selectedForm?.groups) {
      console.log(
        '🔄 usePrefillReservedFields: No form definition, returning empty'
      )
      return { prefilledAnswers, disabledFieldIds }
    }

    // Use safe player object - null/undefined becomes empty object
    const safePlayer = selectedPlayer ?? ({} as SelectedPlayer)

    // Skip prefilling if selectedPlayer is explicitly set to 'new' (creating new player/official)
    const shouldSkipPrefilling = selectedPlayer?.id === 'new'

    if (shouldSkipPrefilling) {
      console.log(
        '🔄 usePrefillReservedFields: Selected player is "new", only handling email for player registrations'
      )
    }

    // Determine which section and fields to look for based on registration type
    const targetSectionName = isTeamRep
      ? TEAM_OFFICIALS_SECTION
      : PLAYER_INFO_SECTION
    const fieldMappings = isTeamRep ? TEAM_OFFICIAL_FIELDS : PLAYER_INFO_FIELDS

    console.log('🔄 usePrefillReservedFields: Using configuration', {
      targetSectionName,
      fieldMappings,
    })

    // Find the target reserved section
    const targetSection = selectedForm.groups.find(
      (group) =>
        isFieldReserved(group.is_reserved) && group.name === targetSectionName
    )

    if (!targetSection) {
      console.log('🔄 usePrefillReservedFields: Target section not found', {
        targetSectionName,
        availableSections: selectedForm.groups.map((g) => ({
          name: g.name,
          is_reserved: isFieldReserved(g.is_reserved),
        })),
      })
      return { prefilledAnswers, disabledFieldIds }
    }

    console.log('🔄 usePrefillReservedFields: Found target section', {
      sectionName: targetSection.name,
      itemCount: targetSection.items.length,
    })

    // Process each field mapping
    fieldMappings.forEach((mapping: ReservedField) => {
      // Find the corresponding form item
      const formItem = targetSection.items.find(
        (item) =>
          isFieldReserved(item.is_reserved) && item.name === mapping.fieldName
      )

      if (!formItem) {
        console.log('🔄 usePrefillReservedFields: Form item not found', {
          fieldName: mapping.fieldName,
          availableItems: targetSection.items.map((i) => ({
            name: i.name,
            is_reserved: isFieldReserved(i.is_reserved),
          })),
        })
        return
      }

      // Get the value from selectedPlayer or use logged user data for specific fields
      let valueToUse: any
      if (mapping.playerKey === 'email' && loggedUserEmail) {
        // Always use logged user email for Player Email field (for both team rep and player)
        valueToUse = loggedUserEmail
        console.log('🔄 usePrefillReservedFields: Using logged user email', {
          fieldName: mapping.fieldName,
          loggedUserEmail,
          isTeamRep,
        })
      } else if (
        mapping.playerKey === 'birth_date' &&
        isTeamRep &&
        (!safePlayer[mapping.playerKey] ||
          safePlayer[mapping.playerKey] === '') &&
        loggedUserBirthDate
      ) {
        // For team rep, use logged user birth date if selected player has no birth date
        valueToUse = loggedUserBirthDate
        console.log(
          '🔄 usePrefillReservedFields: Using logged user birth date for team rep',
          {
            fieldName: mapping.fieldName,
            loggedUserBirthDate,
            selectedPlayerBirthDate: safePlayer[mapping.playerKey],
          }
        )
      } else if (!shouldSkipPrefilling) {
        // Only use player data if not explicitly creating new player/official
        valueToUse = safePlayer[mapping.playerKey]
      }

      // Special handling for email field - always disable it when logged user email is available
      if (mapping.playerKey === 'email' && loggedUserEmail) {
        // Always disable email field for player registrations when logged user email is available
        disabledFieldIds.add(formItem.id)
      }

      if (
        valueToUse !== undefined &&
        valueToUse !== null &&
        valueToUse !== ''
      ) {
        let processedValue = valueToUse

        // Special handling for birth_date fields - convert ISO date to YYYY-MM-DD format
        if (
          mapping.playerKey === 'birth_date' &&
          typeof valueToUse === 'string'
        ) {
          // Check if it's an ISO date string (contains 'T' for time)
          if (valueToUse.includes('T')) {
            // Extract just the date part (YYYY-MM-DD) from ISO string
            processedValue = valueToUse.split('T')[0]
            console.log('🔄 usePrefillReservedFields: Converted ISO date', {
              original: valueToUse,
              converted: processedValue,
            })
          }
        }

        console.log('🔄 usePrefillReservedFields: Prefilling field', {
          fieldName: mapping.fieldName,
          playerKey: mapping.playerKey,
          value: processedValue,
          itemId: formItem.id,
        })

        prefilledAnswers[formItem.id] = processedValue

        // Commented out because we want to disable all fields for now
        // Disable field if it has a prefilled value (except for email which is handled separately)
        // if (mapping.playerKey !== 'email') {
        disabledFieldIds.add(formItem.id)
        // }
      } else {
        console.log('🔄 usePrefillReservedFields: No value to prefill', {
          fieldName: mapping.fieldName,
          playerKey: mapping.playerKey,
          valueToUse,
        })
      }
    })

    console.log('🔄 usePrefillReservedFields: Final result', {
      prefilledCount: Object.keys(prefilledAnswers).length,
      disabledCount: disabledFieldIds.size,
      prefilledAnswers,
      disabledFieldIds: Array.from(disabledFieldIds),
    })

    return { prefilledAnswers, disabledFieldIds }
    // Dont add the existingAnswers to the deps array because it will cause a infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedForm,
    selectedPlayer,
    isTeamRep,
    loggedUserEmail,
    loggedUserBirthDate,
  ])
}
